import React, { useEffect, useState } from "react";
import { Banknote, Bell, Building, Gauge, HardHat, Info, KeyRound, LayoutGrid, ListOrdered, Palette, PartyPopper, Percent, Plus, Rocket, Save, UserPen, Users, X } from "lucide-react";
import { practiceApi } from "../lib/practiceApi";
import { TeamManager } from "../components/TeamManager";
import { AccountSecurity } from "../components/AccountSecurity";
import { BrandingAssets } from "../components/BrandingAssets";
import { DocumentPalette } from "../components/DocumentPalette";
import { DigestSchedule } from "../components/DigestSchedule";
import { ReleaseAdmin } from "../components/releases/ReleaseAdmin";
import { MomentumSettings } from "../components/momentum/MomentumSettings";
import { InfoCenter } from "../components/releases/InfoCenter";
import { GreetingsSettings } from "../components/settings/GreetingsSettings";
import { newRowId, withRowIds } from "../lib/rowKeys";
import { IssuerSettings, PolicyAssociation } from "../components/quotes/IssuerSettings";
import "./settings.css";

// Liste modificabili: le righe ricevono un id stabile così React non confonde i campi quando l'elenco cambia.
const LIST_FIELDS = { studio: ["signers", "representatives"], fiscal: ["expenses"], catalog: ["items"], safety: ["minimum_requirements"] };
const withGroupIds = (group, data) => (LIST_FIELDS[group] || []).reduce(
  (acc, field) => ({ ...acc, [field]: withRowIds(acc[field]) }), data || {});
const withSettingsIds = all => Object.fromEntries(Object.entries(all || {}).map(([group, data]) => [group, withGroupIds(group, data)]));

const ACCOUNT_TAB = ["account", "Il mio accesso", KeyRound];
const INFO_TAB = ["info", "Informazioni", Info];
const MODULE_LABELS = [["coordination", "Coordinamento sicurezza", "Cantieri CSE/CSP, imprese e documentazione"],
                       ["wells", "Pozzi artesiani", "Casi pozzo, rinnovi quinquennali e letture contatore"],
                       ["pos", "Convenzioni", "Convenzioni, POS e PiMUS con scadenze annuali"]];
const TABS = [
  ["studio", "Dati del soggetto emittente", Building],
  ["palette", "Colori documenti", Palette],
  ["catalog", "Catalogo prestazioni", ListOrdered],
  ["fiscal", "Fisco e documenti", Percent],
  ["banking", "Banca e pagamenti", Banknote],
  ["notifications", "Notifiche email", Bell],
  ["safety", "Sicurezza cantieri", HardHat],
  ["team", "Utenti e ruoli", Users],
  ["momentum", "Momentum e obiettivi", Gauge],
  ["greetings", "Saluti dello studio", PartyPopper],
  ["modules", "Sezioni del workspace", LayoutGrid],
  ["releases", "Gestione aggiornamenti", Rocket],
  INFO_TAB,
  ACCOUNT_TAB,
];

const STUDIO_FIELDS = [
  ["name", "Nome e titolo"], ["birth", "Dati di nascita"], ["fiscal_code", "Codice fiscale"],
  ["residence", "Residenza"], ["office", "Studio"], ["register", "Iscrizione all'ordine"],
];
const COMPANY_FIELDS = [
  ["company_name", "Ragione sociale"], ["company_vat", "Partita IVA"], ["company_fiscal_code", "Codice fiscale"],
  ["company_office", "Sede legale"], ["company_rea", "Registro imprese / REA"], ["company_capital", "Capitale sociale"],
  ["company_pec", "PEC della società"], ["company_policy", "Polizza intestata alla società (se distinta)"],
];
const COMMON_FIELDS = [
  ["place", "Luogo di firma"],
  ["brand_title", "Intestazione PDF"], ["brand_subtitle", "Sottotitolo PDF"],
  ["email", "Email"], ["phone", "Telefono"], ["pec", "PEC"], ["website", "Sito web"],
];
const BANKING_FIELDS = [["holder", "Intestatario"], ["bank", "Banca"], ["iban", "IBAN"], ["bic", "BIC / SWIFT"]];

const ADMIN_ONLY_TABS = ["team", "releases", "modules", "palette", "momentum"];

export default function SettingsPage({ user, initialTab = "studio", onUser, go = () => {} }) {
  const isAdmin = user.role === "admin";
  const [tab, setTab] = useState(isAdmin || !ADMIN_ONLY_TABS.includes(initialTab) ? initialTab : "studio");
  const [settings, setSettings] = useState(null);
  const [docCatalog, setDocCatalog] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  useEffect(() => {
    practiceApi("/settings").then(data => setSettings(withSettingsIds(data))).catch(err => setError(err.message));
    practiceApi("/safety/catalog").then(data => setDocCatalog(data.doc_catalog || {})).catch(() => {});
  }, []);

  if (!settings) return <div className="practice-workspace" data-testid="settings-loading">
    {error ? <div className="error" role="alert" data-testid="settings-error">{error}</div> : <p className="muted">Caricamento impostazioni…</p>}
  </div>;

  const tabs = isAdmin ? TABS : TABS.filter(([key]) => !ADMIN_ONLY_TABS.includes(key));
  const group = settings?.[tab] || {};
  const setValue = (field, value) => setSettings(s => ({ ...s, [tab]: { ...s[tab], [field]: value } }));

  async function save() {
    setBusy(true); setError(""); setSaved("");
    try {
      const updated = await practiceApi(`/settings/group/${tab}`, { method: "PUT", body: JSON.stringify({ data: settings[tab] }) });
      setSettings(s => ({ ...s, [tab]: withGroupIds(tab, updated) }));
      setSaved("Impostazioni salvate");
      setTimeout(() => setSaved(""), 4000);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  const setItem = (index, patch) => setSettings(s => ({ ...s, catalog: { ...s.catalog, items: s.catalog.items.map((item, i) => i === index ? { ...item, ...patch } : item) } }));
  const setRow = (field, index, patch) => setSettings(s => ({ ...s, [tab]: { ...s[tab], [field]: s[tab][field].map((row, i) => i === index ? { ...row, ...patch } : row) } }));
  const removeRow = (field, index) => setSettings(s => ({ ...s, [tab]: { ...s[tab], [field]: s[tab][field].filter((_, i) => i !== index) } }));
  const addRow = (field, row) => setSettings(s => ({ ...s, [tab]: { ...s[tab], [field]: [...(s[tab][field] || []), { ...row, _uid: newRowId() }] } }));

  return <div className="settings-page practice-workspace" data-testid="settings-page">
    <header className="practice-header"><div>
      <span className="practice-kicker">CONFIGURAZIONE STUDIO</span>
      <h1 data-testid="settings-title">Impostazioni</h1>
      <p>{isAdmin ? "Dati, compensi e automatismi usati da preventivi, lettere di incarico e fascicoli." : "Gestisci le credenziali del tuo accesso allo studio."}</p>
    </div></header>

    <div className="settings-layout">
      <nav className="settings-tabs">
        {tabs.map(([key, label, Icon]) => <button key={key} className={tab === key ? "active" : ""} data-testid={`settings-tab-${key}`} onClick={() => { setTab(key); setError(""); setSaved(""); }}>
          <Icon size={16} /><span>{label}</span>
        </button>)}
      </nav>

      <section className="settings-panel">
        {error && <div className="error" role="alert" data-testid="settings-panel-error">{error}</div>}
        {saved && <div className="flash" data-testid="settings-saved">{saved}</div>}

        {tab === "studio" && <>
          <p className="muted">Scegli chi emette preventivi, lettere di incarico e documenti: un professionista o una società. I dati non usati restano salvati.</p>
          <div className="practice-form-grid">
            <label className="field-wide">Tipo di soggetto
              <select data-testid="settings-issuer-type" value={group.issuer_type || "professionista"} onChange={e => setValue("issuer_type", e.target.value)}>
                <option value="professionista">Professionista</option>
                <option value="societa">Società</option>
              </select>
            </label>
          </div>
          {(group.issuer_type || "professionista") === "professionista" ? <>
            <h3>Dati del professionista</h3>
            <div className="practice-form-grid">
              {STUDIO_FIELDS.map(([field, label]) => <label key={field}>{label}
                <input data-testid={`settings-studio-${field}`} value={group[field] || ""} onChange={e => setValue(field, e.target.value)} />
              </label>)}
            </div>
          </> : <>
            <h3>Dati della società</h3>
            <div className="practice-form-grid">
              {COMPANY_FIELDS.map(([field, label]) => <label key={field}>{label}
                <input data-testid={`settings-studio-${field}`} value={group[field] || ""} onChange={e => setValue(field, e.target.value)} />
              </label>)}
            </div>
            <IssuerSettings group={group} setValue={setValue} setRow={setRow} addRow={addRow} removeRow={removeRow} />
          </>}
          <PolicyAssociation group={group} setValue={setValue} />
          <h3>Dati comuni e intestazione dei documenti</h3>
          <div className="practice-form-grid">
            {COMMON_FIELDS.map(([field, label]) => <label key={field}>{label}
              <input data-testid={`settings-studio-${field}`} value={group[field] || ""} onChange={e => setValue(field, e.target.value)} />
            </label>)}
          </div>
          <BrandingAssets studio={group} onChange={setValue} onError={setError}
            onDone={message => { setSaved(message); setTimeout(() => setSaved(""), 4000); }} />
        </>}

        {tab === "modules" && <>
          <p className="muted">Attiva solo le sezioni che servono allo studio: quelle disattivate spariscono dal menu del workspace (i dati restano salvati).</p>
          {MODULE_LABELS.map(([key, label, hint]) => <label className="momentum-switch" key={key} data-testid={`settings-module-${key}`}>
            <input type="checkbox" checked={group[key] !== false} onChange={e => setValue(key, e.target.checked)} />
            <span><strong>{label}</strong><small>{hint}</small></span>
          </label>)}
        </>}

        {tab === "info" && <InfoCenter studio={settings?.studio || {}} go={go} />}

        {tab === "palette" && <DocumentPalette onError={setError}
          onDone={message => { setSaved(message); setTimeout(() => setSaved(""), 4000); }} />}

        {tab === "catalog" && <>
          <p className="muted">Le voci qui elencate popolano ogni nuovo preventivo. Le modifiche non alterano i preventivi già salvati.</p>
          {(group.items || []).map((item, index) => <div className="settings-item" key={item._uid} data-testid={`settings-catalog-item-${index}`}>
            <div className="settings-item-head">
              <input className="settings-item-label" data-testid={`settings-catalog-label-${index}`} value={item.label || ""} onChange={e => setItem(index, { label: e.target.value })} />
              <button className="icon-btn" title="Rimuovi voce" data-testid={`settings-catalog-remove-${index}`} onClick={() => removeRow("items", index)}><X size={14} /></button>
            </div>
            <div className="practice-form-grid">
              <label>Sezione<input data-testid={`settings-catalog-group-${index}`} value={item.group || ""} onChange={e => setItem(index, { group: e.target.value })} /></label>
              <label>Modalità<select data-testid={`settings-catalog-mode-${index}`} value={item.mode || "fixed"} onChange={e => setItem(index, { mode: e.target.value })}>
                <option value="fixed">Importo a corpo</option><option value="percent">Percentuale sul costo lavori</option><option value="qty">Quantità × prezzo</option>
              </select></label>
              {(item.mode || "fixed") === "fixed" && <label>Importo<input type="number" step="0.01" data-testid={`settings-catalog-amount-${index}`} value={item.amount ?? ""} onChange={e => setItem(index, { amount: Number(e.target.value) })} /></label>}
              {item.mode === "percent" && <>
                <label>Percentuale<input type="number" step="0.1" data-testid={`settings-catalog-percent-${index}`} value={item.percent ?? ""} onChange={e => setItem(index, { percent: Number(e.target.value) })} /></label>
                <label>Minimo garantito<input type="number" step="0.01" data-testid={`settings-catalog-min-${index}`} value={item.min_amount ?? ""} onChange={e => setItem(index, { min_amount: Number(e.target.value) })} /></label>
              </>}
              {item.mode === "qty" && <>
                <label>Prezzo unitario<input type="number" step="0.01" data-testid={`settings-catalog-unit-${index}`} value={item.unit_price ?? ""} onChange={e => setItem(index, { unit_price: Number(e.target.value) })} /></label>
                <label>Unità<input data-testid={`settings-catalog-unit-label-${index}`} value={item.unit || ""} onChange={e => setItem(index, { unit: e.target.value })} /></label>
              </>}
              <label className="field-wide">Descrizione<textarea rows={3} data-testid={`settings-catalog-desc-${index}`} value={item.description || ""} onChange={e => setItem(index, { description: e.target.value })} /></label>
            </div>
          </div>)}
          <button className="ghost" data-testid="settings-catalog-add" onClick={() => addRow("items", { key: `voce_${Date.now().toString(36)}`, group: "Onorari in fase di progettazione", label: "Nuova prestazione", mode: "fixed", amount: 0, description: "" })}><Plus size={14} />Aggiungi prestazione</button>
        </>}

        {tab === "fiscal" && <>
          <div className="practice-form-grid">
            <label>I.V.A. %<input type="number" step="0.5" data-testid="settings-fiscal-vat" value={group.vat_rate ?? ""} onChange={e => setValue("vat_rate", Number(e.target.value))} /></label>
            <label>Cassa previdenziale %<input type="number" step="0.5" data-testid="settings-fiscal-cassa" value={group.cassa_rate ?? ""} onChange={e => setValue("cassa_rate", Number(e.target.value))} /></label>
            <label>Tipo di contributo<select data-testid="settings-fiscal-cassa-kind" value={group.cassa_kind || "inarcassa_integrativo"} onChange={e => setValue("cassa_kind", e.target.value)}>
              <option value="inarcassa_integrativo">Contributo integrativo Inarcassa (escluso dalla ritenuta)</option>
              <option value="previdenziale_generico">Contributo previdenziale (incluso nella ritenuta)</option>
              <option value="inps_gestione_separata">Rivalsa INPS gestione separata (inclusa nella ritenuta)</option>
            </select></label>
            <label>Ritenuta d'acconto %<input type="number" step="0.5" min="0" max="100" data-testid="settings-fiscal-withholding" value={group.withholding_rate ?? ""} onChange={e => setValue("withholding_rate", Number(e.target.value))} /></label>
            <label>Prefisso numerazione preventivi<input data-testid="settings-fiscal-prefix" placeholder="es. P-" value={group.quote_prefix || ""} onChange={e => setValue("quote_prefix", e.target.value)} /></label>
            <label>Piano acconti standard (%)<input data-testid="settings-fiscal-advance-plan" placeholder="es. 30, 40, 30" value={group.advance_plan || ""} onChange={e => setValue("advance_plan", e.target.value)} /><small className="practice-source">Il primo valore è la percentuale proposta come acconto quando accetti un preventivo.</small></label>
            <label className="field-wide">Condizioni standard nei preventivi<textarea rows={6} data-testid="settings-fiscal-conditions" value={group.conditions || ""} onChange={e => setValue("conditions", e.target.value)} /></label>
          </div>
          <h3>Spese accessorie standard</h3>
          {(group.expenses || []).map((row, index) => <div className="settings-row" key={row._uid} data-testid={`settings-expense-${index}`}>
            <input data-testid={`settings-expense-label-${index}`} value={row.label || ""} onChange={e => setRow("expenses", index, { label: e.target.value })} />
            <input type="number" step="0.01" data-testid={`settings-expense-amount-${index}`} value={row.amount ?? ""} onChange={e => setRow("expenses", index, { amount: Number(e.target.value) })} />
            <button className="icon-btn" title="Rimuovi spesa" data-testid={`settings-expense-remove-${index}`} onClick={() => removeRow("expenses", index)}><X size={13} /></button>
          </div>)}
          <button className="ghost" data-testid="settings-expense-add" onClick={() => addRow("expenses", { label: "", amount: 0 })}><Plus size={14} />Aggiungi spesa standard</button>
        </>}

        {tab === "banking" && <div className="practice-form-grid">
          {BANKING_FIELDS.map(([field, label]) => <label key={field}>{label}
            <input data-testid={`settings-banking-${field}`} value={group[field] || ""} onChange={e => setValue(field, e.target.value)} />
          </label>)}
          <label className="field-wide">Condizioni di pagamento<textarea rows={3} data-testid="settings-banking-terms" value={group.payment_terms || ""} onChange={e => setValue("payment_terms", e.target.value)} /></label>
        </div>}

        {tab === "notifications" && <>
          <div className="practice-notice" data-testid="settings-notifications-note"><span>Ogni email parte con il nome mittente qui indicato e con la tua email come indirizzo di risposta: chi riceve vede il tuo studio e risponde direttamente a te. L'indirizzo tecnico di spedizione è quello della piattaforma (condiviso e già verificato); per spedire da una casella del tuo dominio serve collegare il dominio dello studio.</span></div>
          <div className="practice-form-grid">
            <label className="quote-switch">Notifiche email attive<input type="checkbox" data-testid="settings-notifications-enabled" checked={!!group.enabled} onChange={e => setValue("enabled", e.target.checked)} /></label>
            <label>Nome mittente visualizzato<input data-testid="settings-notifications-from-name" placeholder="Studio Ing. Mario Rossi" value={group.from_name || ""} onChange={e => setValue("from_name", e.target.value)} /></label>
            <label>Email dello studio (usata come risposta)<input data-testid="settings-notifications-from-email" placeholder="studio@tuodominio.it" value={group.from_email || ""} onChange={e => setValue("from_email", e.target.value)} /></label>
            <label>Rispondi a (se diversa)<input data-testid="settings-notifications-reply" value={group.reply_to || ""} onChange={e => setValue("reply_to", e.target.value)} /></label>
            <label>Giorni di preavviso scadenze<input data-testid="settings-notifications-days" placeholder="30, 15, 7" value={group.days_before || ""} onChange={e => setValue("days_before", e.target.value)} /></label>
            <label className="quote-switch">Avvisami quando un incarico viene firmato<input type="checkbox" data-testid="settings-notifications-signature" checked={!!group.notify_signature} onChange={e => setValue("notify_signature", e.target.checked)} /></label>
            <label className="quote-switch">Promemoria scadenze pratiche<input type="checkbox" data-testid="settings-notifications-deadlines" checked={!!group.notify_deadlines} onChange={e => setValue("notify_deadlines", e.target.checked)} /></label>
          </div>
          <h3 style={{ margin: "26px 0 10px", fontSize: 13 }}>Riepilogo settimanale delle scadenze</h3>
          <div className="practice-form-grid">
            <label className="quote-switch">Invia il riepilogo settimanale<input type="checkbox" data-testid="settings-digest-enabled" checked={group.weekly_digest !== false} onChange={e => setValue("weekly_digest", e.target.checked)} /></label>
            <label>Giorno di invio
              <select data-testid="settings-digest-day" value={group.digest_day ?? 0} onChange={e => setValue("digest_day", Number(e.target.value))}>
                {["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"].map((day, index) => <option key={day} value={index}>{day}</option>)}
              </select>
            </label>
            <label>Ora di invio
              <select data-testid="settings-digest-hour" value={group.digest_hour ?? 8} onChange={e => setValue("digest_hour", Number(e.target.value))}>
                {Array.from({ length: 24 }, (_, hour) => <option key={hour} value={hour}>{String(hour).padStart(2, "0")}:00</option>)}
              </select>
            </label>
            <label>Scadenze da includere (giorni)<input type="number" min="1" max="365" data-testid="settings-digest-horizon" value={group.horizon_days ?? 30} onChange={e => setValue("horizon_days", Number(e.target.value))} /></label>
            <label>Destinatari
              <select data-testid="settings-digest-recipients" value={group.digest_recipients || "responsabili"} onChange={e => setValue("digest_recipients", e.target.value)}>
                <option value="responsabili">Ogni responsabile + riepilogo all'amministratore</option>
                <option value="solo_admin">Solo amministratore</option>
              </select>
            </label>
          </div>
          <DigestSchedule onError={setError} onDone={message => { setSaved(message); setTimeout(() => setSaved(""), 5000); }} />
        </>}

        {tab === "safety" && <>
          <p className="muted">Requisiti documentali minimi proposti quando si aggiunge un'impresa in cantiere.</p>
          {(group.minimum_requirements || []).map((row, index) => <div className="settings-row" key={row._uid} data-testid={`settings-requirement-${index}`}>
            <select data-testid={`settings-requirement-type-${index}`} value={row.doc_type || ""} onChange={e => setRow("minimum_requirements", index, { doc_type: e.target.value, label: docCatalog[e.target.value] || row.label })}>
              <option value="">Seleziona documento</option>
              {Object.entries(docCatalog).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
            <input data-testid={`settings-requirement-label-${index}`} value={row.label || ""} onChange={e => setRow("minimum_requirements", index, { label: e.target.value })} />
            <button className="icon-btn" title="Rimuovi requisito" data-testid={`settings-requirement-remove-${index}`} onClick={() => removeRow("minimum_requirements", index)}><X size={13} /></button>
          </div>)}
          <button className="ghost" data-testid="settings-requirement-add" onClick={() => addRow("minimum_requirements", { doc_type: "", label: "" })}><Plus size={14} />Aggiungi requisito</button>
        </>}

        {tab === "momentum" && <div className="settings-team" data-testid="settings-momentum-panel"><MomentumSettings /></div>}
        {tab === "releases" && <div className="settings-team" data-testid="settings-releases-panel"><ReleaseAdmin /></div>}
        {tab === "team" && <div className="settings-team" data-testid="settings-team-panel">
          <TeamManager currentUserId={user.id} />
        </div>}

        {tab === "greetings" && <GreetingsSettings isAdmin={isAdmin} />}

        {tab === "account" && <div className="settings-team" data-testid="settings-account-panel">
          <AccountSecurity user={user} onUser={onUser} />
        </div>}

        {!["team", "account", "palette", "momentum", "releases", "greetings"].includes(tab) && <div className="settings-actions">
          <button className="primary" data-testid="settings-save" disabled={busy} onClick={save}><Save size={15} />{busy ? "Salvataggio…" : "Salva impostazioni"}</button>
        </div>}
      </section>
    </div>
  </div>;
}
